import { observer } from 'mobx-react-lite';
import {
  Button,
  CheckboxField,
  Column,
  DateField,
  Icon,
  SelectField,
  showMessageBox,
  TextAutocompleteField,
  TextField,
} from '../../../components';
import { editableColumn } from '../../../components/grid/columns/editableColumn';
import { useStore } from '../../../store';
import type { EventOrValue } from '../../../store/utils/extractValue';
import type Promotion from '../store/Promotion';

export default function createColumns(): Column<Promotion>[] {
  return [
    editableColumn({
      header: 'Product',
      id: 'sellableName',
      minWidth: 176,
      maxWidth: 304,
      renderEditor(promotion: Promotion) {
        return (
          <SelectField
            errorText={promotion.errors.sellableId}
            name="sellableId"
            onChange={promotion.handleChange}
            options={promotion.sellableOptions}
            sx={{ mt: 0, width: 'auto' }}
            value={promotion.sellableId || ''}
          />
        );
      },
    }),
    editableColumn({
      header: 'Starting Date',
      id: 'displayStartDate',
      minWidth: 144,
      renderEditor(promotion: Promotion) {
        return (
          <DateField
            errorText={promotion.errors.startDate}
            name="startDate"
            onChange={promotion.handleChange as (e: EventOrValue) => void}
            sx={{ mt: 0 }}
            value={promotion.startDate}
          />
        );
      },
    }),
    editableColumn({
      header: 'Ending Date',
      id: 'displayEndDate',
      minWidth: 144,
      renderEditor(promotion: Promotion) {
        return (
          <DateField
            errorText={promotion.errors.endDate}
            name="endDate"
            onChange={promotion.handleChange as (e: EventOrValue) => void}
            sx={{ mt: 0 }}
            value={promotion.endDate}
          />
        );
      },
    }),
    editableColumn({
      header: 'Label',
      id: 'label',
      renderEditor(promotion: Promotion) {
        return <LabelSelect promotion={promotion} />;
      },
    }),
    {
      header: 'Price',
      id: 'displayPrice',
      textAlign: 'right',
    },
    {
      header: 'Discount',
      id: 'displayDiscount',
      textAlign: 'right',
    },
    editableColumn({
      header: 'Sale price',
      id: 'displaySalePrice',
      minWidth: 72,
      textAlign: 'right',
      renderEditor(promotion: Promotion) {
        return (
          <TextField
            errorText={promotion.errors.price}
            name="price"
            onChange={promotion.handleChange}
            startAdornment={promotion.sellable.displayCurrency}
            sx={{ mt: 0, width: 72 }}
            type="number"
            value={promotion.price}
          />
        );
      },
    }),
    editableColumn({
      header: 'Pulse Store',
      id: 'applyForPulseStore',
      minWidth: 66,
      textAlign: 'center',
      format(record) {
        if (record.applyForPulseStore) {
          return <Icon icon="check" />;
        }

        return <Icon icon="minus" />;
      },
      renderEditor(promotion: Promotion) {
        return (
          <CheckboxField
            name="applyForPulseStore"
            onChange={promotion.handleChange}
            sx={{
              'display': 'inline-block',
              'mt': 0,
              'width': 66,
              '& label': {
                mr: 0,
              },
            }}
            tooltip="Ticking this box matches the promotion in the pulse store if you sell your products there"
            value={promotion.applyForPulseStore}
          />
        );
      },
    }),
    {
      header: '',
      id: 'actions' as any,
      textAlign: 'right',
      width: 144,
      renderCell(promotion: Promotion) {
        return (
          <>
            <EditButton promotion={promotion} />
            <SaveButton promotion={promotion} />
            <CancelEditButton promotion={promotion} />
            <DeleteButton promotion={promotion} />
          </>
        );
      },
    },
  ];
}

const EditButton = observer(function EditButton({
  promotion,
}: {
  promotion: Promotion;
}) {
  if (promotion.isEditing) {
    return null;
  }

  return (
    <Button
      icon="pen"
      onClick={() => promotion.setEditing(true)}
      size="small"
      type="primaryText"
    >
      Edit
    </Button>
  );
});

const SaveButton = observer(function SaveButton({
  promotion,
}: {
  promotion: Promotion;
}) {
  async function onClick() {
    promotion.setEditing(false);
    await promotion.save();
  }

  if (!promotion.isEditing) {
    return null;
  }

  return (
    <Button
      disabled={promotion.hasErrors}
      disabledTooltip={promotion.allErrorsAsStrings}
      icon="save"
      onClick={onClick}
      size="small"
      type="primaryText"
    >
      Save
    </Button>
  );
});

const CancelEditButton = observer(function CancelEditButton({
  promotion,
}: {
  promotion: Promotion;
}) {
  if (!promotion.isEditing) {
    return null;
  }

  return (
    <Button
      icon="times"
      onClick={promotion.cancelEditing}
      size="small"
      type="secondary"
      variant="text"
    >
      Cancel
    </Button>
  );
});

const DeleteButton = observer(function DeleteButton({
  promotion,
}: {
  promotion: Promotion;
}) {
  if (promotion.isEditing) {
    return null;
  }

  async function onClick() {
    const buttonId = await showMessageBox({
      content: 'Are you sure you want to delete the promotion?',
      title: 'Deleting',
      buttons: [
        { icon: 'trash', id: 'delete', title: 'Delete' },
        { id: 'cancel', title: 'Cancel' },
      ],
    });

    if (buttonId === 'delete') {
      await promotion.delete();
    }
  }

  return (
    <Button icon="trash" onClick={onClick} size="small" type="secondary">
      Delete
    </Button>
  );
});

const LabelSelect = observer(function LabelSelect({
  promotion,
}: {
  promotion: Promotion;
}) {
  const { accountStore } = useStore();

  const options =
    accountStore.account?.configurations.labels.getLabelOptions('') || [];

  return (
    <TextAutocompleteField
      errorText={promotion.errors.label}
      name="label"
      onChange={promotion.handleChange}
      options={options}
      placeholder="Any label"
      sx={{ mt: 0, minWidth: 100 }}
      value={promotion.label}
    />
  );
});
